// ─────────────────────────────────────────────────────────────────────────────
// audio_mixer.ts — Combines named channels into one bus
//
// The mixer is a single GainNode that all channel outputs connect into.
// It exposes `output` for downstream wiring (typically into the effects
// rack). New channels can be registered after construction; the mixer
// just sums whatever's wired in.
//
// Per-channel volume is owned by the channel itself; the mixer only owns
// the sum-bus volume (typically left at 1).
// ─────────────────────────────────────────────────────────────────────────────

import type { AudioChannel } from './audio_channel';

export class AudioMixer {
  /** Connect this to the next stage (effects rack / master). */
  readonly output: GainNode;

  constructor(private readonly _ctx: AudioContext) {
    this.output = _ctx.createGain();
    this.output.gain.value = 1;
  }

  /** Wire a channel's output into the mixer sum-bus. Idempotent — a
   *  channel can be added once; subsequent calls are no-ops. */
  add(channel: AudioChannel): void {
    channel.output.connect(this.output);
  }

  /** Mixer-bus level (rare to touch — channels carry their own volume). */
  setLevel(v: number): void {
    this.output.gain.setTargetAtTime(v, this._ctx.currentTime, 0.005);
  }
}
